import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  alpha,
  Card,
  CircularProgress,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import React from "react";
import { TextContextDetails } from "../components/test-context-list";
import { theme } from "../theme/theme";
import type { Block, TestContext } from "../types";
import {
  type DoneIntent,
  type HistoryItem,
  isActionIntent,
  isDoneIntent,
  isUserInputIntent,
} from "../types/test-agent-2";
import { humanizeText } from "../utils/text";

type TestAgentV2HistoryItemProps<T extends DoneIntent = DoneIntent> = {
  history: HistoryItem<T>;
  blocks: Block[];
  debug?: boolean;
  showTask?: boolean;
  isLast?: boolean;
};

export const HistoryItemWrapper = ({
  history,
  blocks,
  debug,
  showTask = true,
  isLast = false,
}: TestAgentV2HistoryItemProps) => {
  const [expanded, setExpanded] = React.useState(isLast);

  // Update expanded state when isLast changes
  React.useEffect(() => {
    setExpanded(isLast);
  }, [isLast]);

  const messageCount = (history?.messages ?? []).length;
  const isRunning = history.status === "in_progress";

  return (
    <Stack spacing={1}>
      {showTask && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            justifyContent="flex-start"
            sx={{
              minWidth: 0, // This is crucial for flex children to shrink
              flex: 1, // Take available space but allow shrinking
            }}
          >
            {history.status === "in_progress" && <CircularProgress size={10} color="inherit" />}
            {history.status === "completed" && <DoneIcon fontSize="small" color="success" />}
            {history.status === "failed" && <ErrorIcon fontSize="small" color="error" />}
            <Tooltip title={history.task}>
              <Typography
                variant="body2"
                color="text.primary"
                noWrap
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  minWidth: 0, // Allow the Typography to shrink
                }}
              >
                {history.task}
              </Typography>
            </Tooltip>
          </Stack>
          {history.elapsed_ms && (
            <Typography variant="caption" color="text.secondary" minWidth="60px" textAlign="right">
              {Number((history.elapsed_ms / 1000).toFixed(2))}s
            </Typography>
          )}
        </Stack>
      )}
      {history.intents.map((intent, idx) => (
        <Card
          sx={{
            p: 2,
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          }}
          key={idx}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack direction="column" alignItems="flex-start" spacing={1}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {intent.intent.reasoning}
              </Typography>
              {intent.error && (
                <Typography variant="body2" color="error" sx={{ whiteSpace: "pre-wrap" }}>
                  {intent.error}
                </Typography>
              )}
              {isDoneIntent(intent.intent) && (
                <Stack>
                  <Typography
                    variant="caption"
                    color={intent.intent.status === "success" ? "green" : "red"}
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    Task Goal {humanizeText(intent.intent.status)}
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-wrap" }}>
                    {intent.intent.message}
                  </Typography>
                </Stack>
              )}
              {isActionIntent(intent.intent) && (
                <Stack direction="column" alignItems="flex-start" spacing={0}></Stack>
              )}
              {isUserInputIntent(intent.intent) && (
                <Stack direction="column" alignItems="flex-start" spacing={0}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "pre-wrap" }}
                  >
                    Question
                  </Typography>
                  <Typography variant="body2" color="text.primary" sx={{ whiteSpace: "pre-wrap" }}>
                    {intent.intent.question}
                  </Typography>
                </Stack>
              )}
            </Stack>
            {intent.status === "in_progress" && <CircularProgress size={10} color="inherit" />}
          </Stack>
        </Card>
      ))}
      {messageCount > 0 && (
        <Accordion
          expanded={expanded}
          onChange={() => setExpanded(!expanded)}
          sx={{
            backgroundColor: alpha(theme.palette.success.main, 0.05),
            border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
            "&:before": {
              display: "none",
            },
            boxShadow: "none",
            borderRadius: 1,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 48,
              "&.Mui-expanded": {
                minHeight: 48,
              },
              "& .MuiAccordionSummary-content": {
                margin: "12px 0",
              },
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {messageCount} message{messageCount !== 1 ? "s" : ""} from agent
              </Typography>
              {isRunning && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    "@keyframes ellipsis": {
                      "0%, 20%": { content: '""' },
                      "40%, 20%": { content: '"."' },
                      "60%": { content: '". ."' },
                      "80%, 100%": { content: '". . ."' },
                    },
                    "&::after": {
                      content: '"."',
                      animation: "ellipsis 1s infinite",
                    },
                  }}
                />
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1} direction="column" alignItems="stretch">
              {history.messages?.map((msg, idx) => (
                <Stack
                  key={idx}
                  direction="row"
                  spacing={1}
                  flex={1}
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      whiteSpace: "pre-wrap",
                      flexGrow: 1,
                      width: "100%",
                    }}
                  >
                    {msg.message}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      width: "fit-content",
                      whiteSpace: "pre-wrap",
                      fontFamily: "monospace",
                    }}
                  >
                    [{format(new Date(msg.timestamp), "HH:mm:ss")}]
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      )}
      {blocks
        .filter((b) => history.block_ids.includes(b.id))
        .map((block) => {
          return (
            <TextContextDetails
              key={block.id}
              ix={block.id}
              context={
                {
                  ...block,
                  data: block,
                } as unknown as TestContext
              }
            />
          );
        })}
      {debug && (
        <Typography variant="caption" color="text.secondary">
          <pre>{JSON.stringify(history, null, 1)}</pre>
        </Typography>
      )}
    </Stack>
  );
};
